import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { hasPageAccess } from "./utils/permissions";

import Login from "./pages/Login";
import DashboardLayout from "./layouts/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Vehicles from "./pages/Vehicles";
import RoutesPage from "./pages/Routes";
import Students from "./pages/Students";
import Trips from "./pages/Trips";
import ParentsReport from "./pages/ParentsReport";
import TripReport from "./pages/TripReport";
import StudentReport from "./pages/StudentReport";
import Notifications from "./pages/Notifications";
import Users from "./pages/Users";
import Drivers from "./pages/Drivers";
import ReturnTripsMap from "./pages/ReturnTripsMap";
import ParentDashboard from "./pages/ParentDashboard";
import DriverDashboard from "./pages/DriverDashboard";

// ─── Generic role guard (for parent/driver routes) ───────────────────────────
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading Session...
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === "superadmin") return children;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role))
    return <Navigate to="/" replace />;

  return children;
};

// ─── Permission-aware guard for individual admin pages ───────────────────────
// Uses hasPageAccess() so custom permissions override role restrictions.
const PageRoute = ({ children, pageName }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading Session...
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!hasPageAccess(user, pageName)) return <Navigate to="/" replace />;

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Parent / Driver routes */}
          <Route
            path="/parent-dashboard"
            element={
              <ProtectedRoute allowedRoles={["parent"]}>
                <ParentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/driver-dashboard"
            element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin layout — all admin/operator roles enter here */}
          <Route
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin", "operator"]}>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Pages available to all admin roles (sidebar already filters visibility) */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/routes" element={<RoutesPage />} />
            <Route path="/students" element={<Students />} />
            <Route path="/trips" element={<Trips />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/return-trips-map" element={<ReturnTripsMap />} />

            {/* Reports */}
            <Route path="/reports/parents"   element={<PageRoute pageName="Reports - Parents"><ParentsReport /></PageRoute>} />
            <Route path="/reports/trips"     element={<PageRoute pageName="Reports - Trip Report"><TripReport /></PageRoute>} />
            <Route path="/reports/students"  element={<PageRoute pageName="Reports - Student Report"><StudentReport /></PageRoute>} />

            {/* Permission-controlled pages — operator can access if granted */}
            <Route path="/notifications" element={<PageRoute pageName="Notifications"><Notifications /></PageRoute>} />
            <Route path="/users"         element={<PageRoute pageName="Users"><Users /></PageRoute>} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
